<section class="wallet-balance-summary">
    <div class="wallet-balance-summary__content">
        <h2>{{ "Balance" | translate }}</h2>
        <p>{{ (user?.pro ? "Your wallet balance explained" : "Your wallet balance explained personal") | translate }}</p>
    </div>
    <div class="wallet-balance-summary__amount" *ngIf="!loadingWallet; else walletLoader">
        <app-price [price]="wallet.balancePrice" [alwaysShowPrice]="true" [convert]="false"></app-price>
        <div class="wallet-pending-balance text-muted">
            <span>{{ "Pending balance" | translate }}:</span>
            <app-price [price]="wallet.pendingBalancePrice" [alwaysShowPrice]="true" [convert]="false"></app-price>
        </div>
    </div>
</section>

<div class="separator my-4"></div>

<section class="profile-subsection">
    <h2>{{ "Withdraw" | translate }}</h2>
    <p>{{ "Withdraw description" | translate }}</p>
    <ng-container *ngIf="user?.team || user?.status !== 'validated'">
        <em class="text-muted">{{ "Account must be validated to withdraw" | translate }}</em>
    </ng-container>
    <div class="withdraw-form d-flex flex-column flex-sm-row align-items-stretch align-items-sm-end gap-2" *ngIf="user && !user.team && user.status === 'validated'">
        <div class="flex-grow-1">
            <label for="withdrawAmount">{{ "Amount" | translate }}</label>
            <div class="input-group">
                <input
                    type="number"
                    class="form-control"
                    id="withdrawAmount"
                    [(ngModel)]="withdrawAmount"
                    min="1"
                    step="0.01"
                    [disabled]="!canWithdraw"
                />
                <span class="input-group-text">{{ wallet.currency }}</span>
            </div>
        </div>
        <button type="button" class="btn btn-outline-secondary text-nowrap" (click)="setWithdrawAll()" [disabled]="!canWithdraw">
            {{ "Withdraw all" | translate }}
        </button>
        <button type="button" class="btn btn-primary text-nowrap" (click)="requestWithdraw()" [disabled]="!canWithdraw">
            {{ "Request withdraw" | translate }}
        </button>
    </div>
</section>

<div class="separator my-4"></div>

<section class="profile-subsection">
    <h2>{{ "Transaction history" | translate }}</h2>
    <ng-container *ngIf="loadingTransactions; else transactionsContainer">
        <div class="py-4 text-center">
            <app-loader></app-loader>
        </div>
    </ng-container>
    <ng-template #transactionsContainer>
        <div class="table-responsive" *ngIf="transactions.items.length > 0; else noTransactions">
            <table class="table table-sm wallet-history-table">
                <thead>
                    <tr>
                        <th>{{ "Date" | translate }}</th>
                        <th>{{ "Type" | translate }}</th>
                        <th>{{ "Amount" | translate }}</th>
                        <th>{{ "Status" | translate }}</th>
                        <th>{{ "Order" | translate }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let tx of transactions.items">
                        <td [attr.data-label]="'Date' | translate">
                            <span class="wallet-history-table-value">{{ tx.createdAt | date: "dd.MM.yyyy HH:mm" }}</span>
                        </td>
                        <td [attr.data-label]="'Type' | translate">
                            <span class="wallet-history-table-value">{{ tx.type | translate }}</span>
                        </td>
                        <td class="wallet-history-table-amount" [attr.data-label]="'Amount' | translate">
                            <span class="wallet-history-table-value">
                                <app-price
                                    [price]="tx.amountPrice"
                                    [alwaysShowPrice]="true"
                                    [convert]="false"
                                ></app-price>
                            </span>
                        </td>
                        <td [attr.data-label]="'Status' | translate">
                            <span class="wallet-history-table-value">{{ tx.status | translate }}</span>
                        </td>
                        <td [attr.data-label]="'Order' | translate">
                            <span class="wallet-history-table-value">{{ tx.orderRef || "-" }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <app-pagination
                *ngIf="!loadingTransactions && transactions.total > transactions.perPage"
                [total]="transactions.total"
                [perPage]="transactions.perPage"
                [page]="transactions.page"
                (pageChanged)="pageChanged($event)"
            ></app-pagination>
        </div>
        <ng-template #noTransactions>
            <div class="py-4 text-center">
                <em>{{ "No wallet transactions yet" | translate }}</em>
            </div>
        </ng-template>
    </ng-template>
</section>

<div class="separator my-4"></div>

<section class="profile-subsection">
    <h2>{{ "Your cards" | translate }}</h2>
    <p>
        {{ "Your cards will be visible here" | translate }}
    </p>
    <ng-container *ngIf="cards; else cardLoader">
        <div class="payment-card payment-card-item mb-2" *ngFor="let card of cards">
            <div class="payment-card-brand d-flex align-items-center gap-3">
                <span class="bi bi-credit-card-2-front"></span>
                <strong>{{ card.brandFormatted }}</strong>
            </div>
            <div class="payment-card-details d-flex align-items-center justify-content-between gap-3">
                <span class="font-monospace fs-6 text-nowrap">•••• •••• •••• {{ card.digits }}</span>
                <span class="text-muted text-nowrap">{{ card.expiresAt | date: "MM/yy" }}</span>
            </div>
            <div class="payment-card-delete">
                <a class="text-danger clickable" (click)="deleteCard(card)">
                    <span class="bi bi-trash"></span>
                </a>
            </div>
        </div>
        <ng-container *ngIf="cards.length === 0">
            <div class="py-4 text-center">
                <em>{{ "You have no cards yet" | translate }}</em>
            </div>
        </ng-container>
    </ng-container>
</section>

<div class="separator my-4"></div>

<section class="profile-subsection">
    <h2>{{ "Your iban" | translate }}</h2>
    <p>
        {{ "Your iban description" | translate }}
    </p>
    <ng-container *ngIf="loadingIban; else ibanContainer">
        <div class="py-4 text-center">
            <app-loader></app-loader>
        </div>
    </ng-container>
    <ng-template #ibanContainer>
        <label for="iban">{{ "Iban" | translate }}</label>
        <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3 w-100 iban-row">
            <div class="flex-grow-1 min-w-0" [formGroup]="ibanForm">
                <input
                    type="text"
                    class="form-control iban-input"
                    id="iban"
                    formControlName="iban"
                    [ngClass]="{ 'is-invalid': ibanForm.get('iban').invalid && ibanForm.get('iban').touched }"
                    placeholder="{{ 'Iban example' | translate }}"
                />
            </div>
            <div class="d-flex justify-content-end">
                <button class="btn btn-primary text-nowrap" (click)="updateIban()">
                    <span class="ti ti-device-floppy fs-5"></span>
                </button>
            </div>
        </div>
    </ng-template>
    <ng-template #cardLoader>
        <div class="py-4 text-center">
            <app-loader></app-loader>
        </div>
    </ng-template>
    <ng-template #walletLoader>
        <div class="py-2 text-center">
            <app-loader></app-loader>
        </div>
    </ng-template>
</section>
